import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CLIENT_SIDEBAR } from '../../constants';
import { useAuth } from '../../context/AuthContext';
import Avatar from '../Avatar';
import NotificationBell from '../NotificationBell';
import SidebarIcon from '../SidebarIcon';

const ClientLayout = ({ title, children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (item) => {
    if (item.path === '/client/dashboard') return location.pathname === item.path;
    if (item.path.startsWith('/client/')) return location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
    return false;
  };

  return (
    <div className="min-h-screen bg-slate-100 font-dm text-giggs-ink">
      <title>GIGGS — {title}</title>
      <link rel="icon" href="/images/favicon.png" type="image/png" />

      <aside className={`fixed inset-y-0 left-0 z-[100] flex w-[260px] flex-col bg-giggs-dark transition-transform duration-300 ease-out lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex items-center justify-between border-b border-white/[0.06] px-6 pb-5 pt-6">
          <Link to="/client/dashboard" className="inline-flex no-underline" aria-label="Client dashboard">
            <img src="/images/logo-white.png" alt="GIGGS" className="h-10 w-auto" />
          </Link>
          <button type="button" className="flex cursor-pointer items-center justify-center border-0 bg-transparent p-1 text-white/50 transition-colors hover:text-white lg:hidden" onClick={() => setSidebarOpen(false)} aria-label="Close navigation">
            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>

        <div className="border-b border-white/[0.06] px-5 py-4">
          <p className="m-0 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/30">Client portal</p>
          <p className="mb-0 mt-1 truncate text-sm font-semibold text-white/80">{user?.name || 'My Giggs account'}</p>
        </div>

        <nav className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-3 py-4">
          {CLIENT_SIDEBAR.map((item) => {
            const active = isActive(item);
            return (
              <Link
                key={item.label}
                to={item.path}
                className={`flex items-center gap-3.5 rounded-xl px-4 py-3 text-sm font-medium no-underline transition-all duration-200 ${active ? 'bg-[rgba(0,166,81,0.15)] text-white [&_svg]:text-giggs-mint' : 'text-white/50 hover:bg-white/[0.05] hover:text-white/85'}`}
                onClick={() => setSidebarOpen(false)}
              >
                <SidebarIcon type={item.icon} />
                <span className="min-w-0 flex-1 truncate">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="border-t border-white/[0.06] px-3 pb-6 pt-4">
          <Link to="/" className="mb-1 flex items-center gap-3.5 rounded-xl px-4 py-3 text-sm font-medium text-white/45 no-underline transition-all hover:bg-white/[0.05] hover:text-white/80">
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 11l9-8 9 8" /><path d="M5 10v10h14V10" /></svg>
            <span>Back to website</span>
          </Link>
          <button type="button" className="flex w-full cursor-pointer items-center gap-3.5 rounded-xl border-0 bg-transparent px-4 py-3 text-left text-sm font-medium text-white/45 transition-all duration-200 hover:bg-red-500/[0.08] hover:text-red-500" onClick={handleLogout}>
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" /></svg>
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {sidebarOpen && <button type="button" className="fixed inset-0 z-[99] cursor-default border-0 bg-black/40 p-0 lg:hidden" onClick={() => setSidebarOpen(false)} aria-label="Close navigation overlay" />}

      <div className="flex min-h-screen min-w-0 flex-col lg:ml-[260px]">
        <header className="sticky top-0 z-50 flex min-h-[75px] items-center gap-4 border-b border-slate-200 bg-white px-4 py-[18px] sm:px-5 lg:px-8">
          <button type="button" className="flex cursor-pointer items-center justify-center border-0 bg-transparent p-1 text-giggs-ink lg:hidden" onClick={() => setSidebarOpen(true)} aria-label="Open navigation">
            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 6h16M4 12h16M4 18h16" /></svg>
          </button>
          <h1 className="m-0 min-w-0 flex-1 truncate font-nunito text-[clamp(1.125rem,2vw,1.375rem)] font-bold text-giggs-ink">{title}</h1>
          <div className="flex shrink-0 items-center gap-3 sm:gap-3.5">
            <NotificationBell />
            {user && <span className="hidden max-w-[180px] truncate text-[13px] font-normal text-[#6B7F75] sm:inline">{user.name}</span>}
            <Avatar user={user} size={38} gradient="linear-gradient(135deg, #C7E8D4, #AAF0C4)" />
          </div>
        </header>
        <div className="min-w-0 flex-1 p-4 sm:p-5 lg:p-8">{children}</div>
      </div>
    </div>
  );
};

export default ClientLayout;
